/*
 * Copyright © 2024-2026 symecode.com. All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution is prohibited.
 */


import React, { useState, useEffect } from 'react';
import BackButton from '../common/BackButton';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import apiClient from '../../services/api';
import { BidCalendarItem, ParticipantType } from '../../types';
import './CompanyCalendar.css';

interface CalendarData {
  year: number;
  month: number;
  companyId: number;
  companyName: string;
  bidsByDate: Record<string, BidCalendarItem[]>;
}

type FilterType = 'all' | 'not_submitted' | 'submitted' | 'accepted' | 'submitted_not_accepted';

const CompanyCalendar: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [calendarData, setCalendarData] = useState<CalendarData | null>(null);
  const [currentYear, setCurrentYear] = useState<number>(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState<number>(new Date().getMonth() + 1);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterType>('all');

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  useEffect(() => {
    fetchCalendarData(currentYear, currentMonth);
  }, [currentYear, currentMonth]);

  const fetchCalendarData = async (year: number, month: number) => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.getCompanyCalendar(year, month);

      if (response.success && response.calendar) {
        setCalendarData(response.calendar);
      } else {
        setError(response.error || 'Failed to load calendar data');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'An error occurred while fetching the calendar');
      console.error('Error fetching calendar:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePreviousMonth = () => {
    if (currentMonth === 1) {
      setCurrentMonth(12);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 12) {
      setCurrentMonth(1);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const handleBidClick = (bidId: number) => {
    // Determine navigation path based on user role
    const navigateTo = (currentUser?.type === ParticipantType.MANAGER ||
                        currentUser?.type === ParticipantType.ADMIN ||
                        currentUser?.type === ParticipantType.EXECUTIVE)
      ? `/bids/${bidId}/view`
      : `/bids/${bidId}/edit`;

    navigate(navigateTo);
  };

  const filterBids = (bids: BidCalendarItem[]): BidCalendarItem[] => {
    if (filter === 'all') return bids;

    return bids.filter(bid => {
      switch (filter) {
        case 'not_submitted':
          return !bid.submitted;
        case 'submitted':
          return bid.submitted === true;
        case 'accepted':
          return bid.accepted === true;
        case 'submitted_not_accepted':
          return bid.submitted === true && bid.accepted !== true;
        default:
          return true;
      }
    });
  };

  const getDaysInMonth = (year: number, month: number): number => {
    return new Date(year, month, 0).getDate();
  };

  const getFirstDayOfMonth = (year: number, month: number): number => {
    return new Date(year, month - 1, 1).getDay();
  };

  const renderCalendar = () => {
    if (!calendarData) return null;

    const daysInMonth = getDaysInMonth(currentYear, currentMonth);
    const firstDay = getFirstDayOfMonth(currentYear, currentMonth);
    const days: React.ReactElement[] = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }

    // Add cells for each day of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateKey = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const bidsForDay = calendarData.bidsByDate[dateKey] || [];
      const filteredBids = filterBids(bidsForDay);

      days.push(
        <div key={day} className="calendar-day">
          <div className="day-number">{day}</div>
          <div className="day-bids">
            {filteredBids.map((bid) => (
              <div
                key={bid.id}
                className={`bid-item ${bid.submitted ? 'submitted' : ''} ${bid.accepted ? 'accepted' : ''}`}
                onClick={() => handleBidClick(bid.id)}
                title={`${bid.name} - ${bid.authorName}${bid.price ? ` - $${bid.price}` : ''}${bid.submitted ? ' (Submitted)' : ''}${bid.accepted ? ' (Accepted)' : ''}`}
              >
                <div className="bid-name">{bid.name}</div>
                <div className="bid-author">{bid.authorName}</div>
                {bid.price && <div className="bid-price">${bid.price.toFixed(2)}</div>}
                {bid.submitted && <div className="bid-status">✓ Submitted</div>}
                {bid.accepted && <div className="bid-status">✓ Accepted</div>}
              </div>
            ))}
          </div>
        </div>
      );
    }

    return days;
  };

  if (loading) {
    return (
      <div className="company-calendar">
      <BackButton to="/" />
        <div className="loading">Loading calendar...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="company-calendar">
        <BackButton to="/" />
        <div className="error">{error}</div>
      </div>
    );
  }

  return (
    <div className="company-calendar">
      <BackButton to="/" />
      <div className="calendar-header">
        <h2>Company Calendar - {calendarData?.companyName}</h2>
        <div className="calendar-controls">
          <button onClick={handlePreviousMonth} className="btn btn-secondary">
            &lt; Previous
          </button>
          <span className="current-month">
            {monthNames[currentMonth - 1]} {currentYear}
          </span>
          <button onClick={handleNextMonth} className="btn btn-secondary">
            Next &gt;
          </button>
        </div>
        <div className="calendar-filters">
          <label>Filter: </label>
          <select value={filter} onChange={(e) => setFilter(e.target.value as FilterType)} className="filter-select">
            <option value="all">All Bids</option>
            <option value="not_submitted">Not Submitted</option>
            <option value="submitted">Submitted</option>
            <option value="accepted">Accepted</option>
            <option value="submitted_not_accepted">Submitted but Not Accepted</option>
          </select>
        </div>
      </div>

      <div className="calendar-grid">
        <div className="calendar-weekdays">
          <div className="weekday">Sunday</div>
          <div className="weekday">Monday</div>
          <div className="weekday">Tuesday</div>
          <div className="weekday">Wednesday</div>
          <div className="weekday">Thursday</div>
          <div className="weekday">Friday</div>
          <div className="weekday">Saturday</div>
        </div>
        <div className="calendar-days">
          {renderCalendar()}
        </div>
      </div>
    </div>
  );
};

export default CompanyCalendar;
